import type { Extension } from '@codemirror/state';
import { python } from '@codemirror/lang-python';
import { markdown } from '@codemirror/lang-markdown';
import { json } from '@codemirror/lang-json';
import { yaml } from '@codemirror/lang-yaml';

export type EditorFileKind = 'markdown' | 'source' | 'plaintext';

const EXTENSION_LANGUAGES: Record<string, () => Extension> = {
  py: python,
  pyi: python,
  md: markdown,
  markdown: markdown,
  json: json,
  ipynb: json,
  yaml: yaml,
  yml: yaml,
};

export const SUPPORTED_TEXT_EXTENSIONS = new Set<string>([
  'py', 'pyi',
  'md', 'markdown',
  'json',
  'yaml', 'yml',
  'txt', 'log', 'cfg', 'conf', 'ini', 'env',
  'r', 'rs', 'go', 'js', 'ts', 'jsx', 'tsx',
  'sh', 'bash', 'zsh',
  'toml',
  'html', 'css',
  'csv', 'tsv',
  'gitignore', 'dockerignore',
  'makefile',
]);

function extOf(filename: string): string {
  const base = filename.split('/').pop() ?? filename;
  const lower = base.toLowerCase();
  if (!lower.includes('.')) return lower;
  return lower.split('.').pop() ?? '';
}

export function getLanguageForFile(filename: string): Extension | null {
  const factory = EXTENSION_LANGUAGES[extOf(filename)];
  return factory ? factory() : null;
}

export function getFileKind(filename: string): EditorFileKind {
  const ext = extOf(filename);
  if (ext === 'md' || ext === 'markdown') return 'markdown';
  if (ext in EXTENSION_LANGUAGES) return 'source';
  return 'plaintext';
}

export function isEditableTextFile(filename: string): boolean {
  return SUPPORTED_TEXT_EXTENSIONS.has(extOf(filename));
}
